import React from "react";
import AdminAvatar from "./AdminAvatar";
import AdminModal from "./modals/AdminModal";
import Button from "components/button/Button";
import CommonModal from "components/modals/Modal";
import { TiPlus } from "react-icons/ti";
import { toast } from "react-hot-toast";
import { createPortal } from "react-dom";
import { Actions } from "constants/actions";
import { useNavigate, useParams } from "react-router-dom";
import { Skeleton, Spinner, useDisclosure } from "@chakra-ui/react";
import { IBranch, IBranchAdmin } from "../types/branch.interface";
import {
  deleteBranchAdmin,
  getBranch,
  getBranchAdmins,
  getBranches,
  moveBranchAdmin,
} from "api/BranchAPI";
import { FaStore } from "react-icons/fa";

const BranchProfile: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [branch, setBranch] = React.useState<IBranch>(null);
  const [branches, setBranches] = React.useState<IBranch[]>(null);
  const [selectedBranch, setSelectedBranch] = React.useState<IBranch>(null);
  const [admins, setAdmins] = React.useState<IBranchAdmin[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isBranchesLoading, setIsBranchesLoading] =
    React.useState<boolean>(true);
  const [modal, setModal] = React.useState<{
    data: any;
    target: string;
    action: Actions;
  }>(null);

  const showModal = (data: any, target: string, action: Actions) => {
    onOpen();
    if (action === Actions.MOVE) getBranchesData();
    setModal({
      data,
      target,
      action,
    });
  };

  const getBranchDetail = () => {
    const promise = getBranch(slug);

    promise
      .then((res) => {
        setBranch(res.data?.data);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(
          err?.response?.data?.message || "Gagal mengambil data toko!"
        );
      });
  };

  const getBranchesData = () => {
    const promise = getBranches();
    promise
      .then((res) => {
        if (res.status === 200) {
          setBranches(res.data?.data);
        }
        setIsBranchesLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(
          err?.response?.data?.message || "Gagal mengambil data toko!"
        );
      });
  };

  const getBranchAdmin = () => {
    const promise = getBranchAdmins(slug);
    promise
      .then((res) => {
        if (res.status === 200) {
          setAdmins(res?.data?.data?.admins);
        }
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message || "Gagal mengambil data admin!"
        );
      });
  };

  const refreshBranchDetail = () => {
    getBranchDetail();
    getBranchAdmin();
  };

  const handleDeleteAdmin = (id: number) => {
    setIsLoading(true);
    const promise = deleteBranchAdmin(slug, id);
    promise
      .then((res) => {
        if (res.status === 200) {
          setIsLoading(false);
          toast.success("Berhasil menghapus admin!");
          refreshBranchDetail();
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Gagal menghapus admin!");
      });
  };

  const handleSelectBranch = (item: IBranch) => {
    setSelectedBranch(item);
  };

  const handleMoveAdmin = () => {
    setIsLoading(true);
    const payload = {
      store_id: selectedBranch?.id,
      user_id: modal?.data?.id,
    };
    const promise = moveBranchAdmin(payload);
    promise
      .then((res) => {
        if (res.status === 200) {
          setIsLoading(false);
          toast.success(
            `Berhasil memindahkan admin ke ${selectedBranch?.name}!`
          );
          refreshBranchDetail();
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Gagal menghapus admin!");
      });
  };

  React.useEffect(() => {
    if (slug) {
      getBranchDetail();
      getBranchAdmin();
    } else {
      toast.error("Toko Cabang tidak ditemukan!");
      navigate("/admin/branches");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="mt-5">
        <div className="my-10">
          {isLoading ? (
            <Skeleton height={7} width={250} rounded={5} />
          ) : (
            <h5 className="text-lg font-bold text-navy-700 dark:text-white">
              {branch?.name}
            </h5>
          )}
          {isLoading ? (
            <Skeleton height={4} width={175} rounded={5} className="mt-2" />
          ) : (
            <p className="mt-2 text-sm text-gray-600">
              OPEN
              <span className="ml-1 font-medium tracking-wide text-brand-500 hover:text-brand-500 dark:text-white">
                {branch?.open_at} - {branch?.close_at}
              </span>
            </p>
          )}
          {isLoading ? (
            <Skeleton height={7} width={500} rounded={5} className="mt-5" />
          ) : (
            <>
              <p className="mb-2 mt-5">{branch?.address}</p>
              <a
                href={`https://maps.google.com/?q=${branch?.latitude},${branch?.longitude}`}
                target="_blank"
                rel="noreferrer"
                className={`text-brand-500 hover:text-brand-500 dark:text-white`}
              >
                Lihat Lokasi
              </a>
            </>
          )}
        </div>
        <div className="flex w-full justify-between">
          <p className="font-semibold text-gray-800">Admin</p>
          <div className="flex gap-2">
            <Button size="fit" children="Lihat" variant="text-only" />
            <Button
              size="fit"
              variant="primary"
              children={
                <div className="flex items-center gap-1">
                  <TiPlus /> Admin
                </div>
              }
              onClick={() => showModal(null, "admin", Actions.CREATE)}
            />
          </div>
        </div>
        {admins?.length > 0 ? (
          <div className="mb-10 flex items-start gap-3">
            {admins?.map((admin, idx) => (
              <AdminAvatar
                name={admin?.name}
                email={admin?.email}
                key={idx}
                avatar={admin?.avatar}
                role={admin?.role}
                id={admin?.id}
                handleUpdate={() => showModal(admin, "admin", Actions.UPDATE)}
                handleMove={() => showModal(admin, "admin", Actions.MOVE)}
                handleDelete={() => showModal(admin, "admin", Actions.DELETE)}
              />
            ))}
          </div>
        ) : (
          <div className="mb-10 flex items-start gap-3">
            <p className="w-full text-center text-gray-600 dark:text-white">
              Belum ada admin yang ditambahkan
            </p>
          </div>
        )}
      </div>
      {createPortal(
        <CommonModal
          size="2xl"
          children={
            <div>
              {modal?.target === "admin" && (
                <AdminModal
                  title={
                    <div className="flex flex-col gap-3">
                      {modal.action === Actions.CREATE ? (
                        <p className="text-lg text-navy-700 dark:text-white">
                          Tambah Admin
                        </p>
                      ) : modal.action === Actions.UPDATE ? (
                        <p className="text-lg text-navy-700 dark:text-white">
                          Edit Admin
                        </p>
                      ) : modal.action === Actions.DELETE ? (
                        <p className="text-base text-navy-700 dark:text-white">
                          Hapus{" "}
                          <span className="font-medium text-red-500">
                            {modal.data?.name}
                          </span>
                          ?
                        </p>
                      ) : (
                        <p className="text-lg text-navy-700 dark:text-white">
                          Pindah Admin ke Toko Cabang
                        </p>
                      )}
                    </div>
                  }
                  body={
                    modal.action === Actions.MOVE ? (
                      <section>
                        {isBranchesLoading ? (
                          <div className="mt-5 flex h-28 items-center justify-center">
                            <Spinner size="lg" className="text-brand-400" />
                          </div>
                        ) : (
                          <div className="mt-5 grid grid-cols-2 gap-3">
                            {branches.length > 0 &&
                              branches
                                .filter((item) => item.id !== branch.id)
                                .map((item) => (
                                  <div
                                    key={item.id}
                                    className={`card__product--small ${
                                      selectedBranch?.id === item.id
                                        ? "active"
                                        : ""
                                    }`}
                                    onClick={() => handleSelectBranch(item)}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-50 bg-opacity-25">
                                        <FaStore className="h-6 w-6 text-brand-500" />
                                      </div>
                                      <div className="flex flex-col">
                                        <p className="text-base font-semibold text-gray-800">
                                          {item.name}
                                        </p>
                                        <p className="line-clamp-2 max-w-[180px] text-sm font-medium text-gray-700">
                                          {item.address}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                          </div>
                        )}
                      </section>
                    ) : (
                      <></>
                    )
                  }
                  action={modal.action}
                  data={modal.data}
                  refreshData={refreshBranchDetail}
                  onCancel={onClose}
                  onDelete={() => handleDeleteAdmin(modal.data?.id)}
                  onMove={() => handleMoveAdmin()}
                />
              )}
            </div>
          }
          isOpen={isOpen}
          onClose={onClose}
        />,
        document.body
      )}
    </>
  );
};

export default BranchProfile;
